import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { Group, Object3D } from 'three';

interface VolumeResult {
  volume: number;
  dimensions: {
    width: number;
    height: number;
    depth: number;
  };
}

export const calculateModelVolume = (file: File): Promise<VolumeResult> => {
  return new Promise((resolve, reject) => {
    const fileURL = URL.createObjectURL(file);
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    let loader;
    switch (fileExtension) {
      case 'stl':
        loader = new STLLoader();
        break;
      case 'obj':
        loader = new OBJLoader();
        break;
      case 'gltf':
      case 'glb':
        loader = new GLTFLoader();
        break;
      default:
        reject(new Error('Unsupported file format'));
        return;
    }

    loader.load(fileURL, (result) => {
      let geometry: THREE.BufferGeometry;
      
      if (result instanceof THREE.BufferGeometry) {
        // STL files return BufferGeometry directly
        geometry = result;
      } else if ((result as GLTF).scene) {
        // GLTF files return a GLTF object with a scene
        const gltfResult = result as GLTF;
        const mesh = gltfResult.scene.children.find((child: Object3D) => child instanceof THREE.Mesh) as THREE.Mesh;
        if (!mesh) {
          reject(new Error('No mesh found in the GLTF model'));
          return;
        }
        geometry = mesh.geometry;
      } else if (result instanceof Group) {
        // OBJ files return a Group
        const mesh = result.children.find((child: Object3D) => child instanceof THREE.Mesh) as THREE.Mesh;
        if (!mesh) {
          reject(new Error('No mesh found in the OBJ model'));
          return;
        }
        geometry = mesh.geometry;
      } else {
        reject(new Error('Unsupported model format'));
        return;
      }

      // Calculate volume
      const volume = calculateMeshVolume(geometry) / 1000 // MM to CM;
      
      // Calculate bounding box dimensions
      geometry.computeBoundingBox();
      const boundingBox = geometry.boundingBox;
      if (!boundingBox) {
        reject(new Error('Could not compute bounding box'));
        return;
      }

      const dimensions = {
        width: boundingBox.max.x - boundingBox.min.x,
        height: boundingBox.max.y - boundingBox.min.y,
        depth: boundingBox.max.z - boundingBox.min.z
      };

      // Clean up
      URL.revokeObjectURL(fileURL);
      
      resolve({ volume, dimensions });
    }, undefined, (error) => {
      URL.revokeObjectURL(fileURL);
      reject(error);
    });
  });
};

function calculateMeshVolume(geometry: THREE.BufferGeometry): number {
  const positions = geometry.getAttribute('position');
  const vertices = positions.array;
  let volume = 0;

  // Calculate volume using signed tetrahedron method
  for (let i = 0; i < positions.count; i += 3) {
    const p1 = new THREE.Vector3(
      vertices[i * 3],
      vertices[i * 3 + 1],
      vertices[i * 3 + 2]
    );
    const p2 = new THREE.Vector3(
      vertices[(i + 1) * 3],
      vertices[(i + 1) * 3 + 1],
      vertices[(i + 1) * 3 + 2]
    );
    const p3 = new THREE.Vector3(
      vertices[(i + 2) * 3],
      vertices[(i + 2) * 3 + 1],
      vertices[(i + 2) * 3 + 2]
    );

    volume += signedVolumeOfTriangle(p1, p2, p3);
  }

  return Math.abs(volume); // Keep in original units
}

function signedVolumeOfTriangle(p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3): number {
  return p1.dot(p2.cross(p3)) / 6.0;
}